import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  AlertCircle,
  CalendarClock,
  CircleDollarSign,
  Loader2,
  RefreshCw,
} from "lucide-react";
import axiosClient from "../../../api/axiosClient";
import { formatMessageRate } from "../../../utils/currencyFormatters";

const CATEGORY_OPTIONS = [
  { key: "all", label: "All" },
  { key: "marketing", label: "Marketing" },
  { key: "utility", label: "Utility" },
  { key: "authentication", label: "Authentication" },
  { key: "service", label: "Service" },
];

const CATEGORY_ORDER = CATEGORY_OPTIONS
  .filter(option => option.key !== "all")
  .map(option => option.key);

const COUNTRY_PLACEHOLDER = "Select Country";
const NO_COUNTRY_SELECTED = "No country selected";

const SILENT_REQUEST_CONFIG = {
  __silentGenericToast: true,
  __silent429: true,
  headers: {
    "x-suppress-generic-toast": "1",
    "x-suppress-429-toast": "1",
  },
};

const extractRatesPayload = response => {
  const payload = response?.data?.payload ?? response?.data?.data ?? [];
  return Array.isArray(payload) ? payload : [];
};

const extractSubscriptionPayload = response =>
  response?.data?.data || response?.data?.payload || response?.data || null;

const normalizeCategory = value => String(value || "").trim().toLowerCase();

const getCountryKey = item =>
  String(item?.countryCode || item?.countryName || "configured-country")
    .trim()
    .toLowerCase();

const formatCategory = value => {
  const normalized = normalizeCategory(value);
  return CATEGORY_OPTIONS.find(option => option.key === normalized)?.label || "-";
};

const formatDate = value => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("D MMM YYYY") : "-";
};

const splitEnumText = value =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();

const formatSubscriptionStatus = value => {
  if (value === null || value === undefined || value === "") return "-";
  const mapped = {
    0: "Trial",
    1: "Active",
    2: "Past Due",
    3: "Grace",
    4: "Cancel At Period End",
    5: "Cancelled",
    6: "Suspended",
    7: "Expired",
  };

  const direct = mapped[value];
  if (direct) return direct;

  return splitEnumText(value);
};

const formatBillingCycle = value => {
  if (value === null || value === undefined || value === "") return "-";
  const mapped = {
    1: "Monthly",
    2: "Yearly",
    3: "Quarterly",
    Monthly: "Monthly",
    Yearly: "Yearly",
    Quarterly: "Quarterly",
  };

  const direct = mapped[value];
  if (direct) return direct;

  return splitEnumText(value);
};

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.errorMessage ||
    error?.message ||
    fallbackMessage
  );
}

function EmptyState() {
  return (
    <div className="p-5">
      <div className="flex min-h-[150px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-8">
        <div className="max-w-md text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <CircleDollarSign className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-slate-900">
            No pricing data is available for this business yet.
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Final WhatsApp rates will appear here after pricing is configured for
            the business country.
          </p>
        </div>
      </div>
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  if (!message) return null;

  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
          <span>{message}</span>
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    </div>
  );
}

function SummaryCell({ label, value }) {
  return (
    <div className="min-w-0 px-4 py-3">
      <div className="truncate text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </div>
      <div className="mt-1 truncate text-sm font-semibold text-slate-950">
        {value || "-"}
      </div>
    </div>
  );
}

function LoadingRows() {
  return (
    <div className="flex min-h-[190px] items-center justify-center px-6 py-10 text-sm text-slate-500">
      <Loader2 className="mr-2 h-4 w-4 animate-spin text-emerald-600" />
      Loading business pricing...
    </div>
  );
}

export default function BusinessRatesPage() {
  const [rates, setRates] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("all");

  const loadPricingData = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    let nextLoadError = "";

    try {
      const [ratesResult, subscriptionResult] = await Promise.allSettled([
        axiosClient.get("/business/billing/rates", SILENT_REQUEST_CONFIG),
        axiosClient.get("/payment/subscription", SILENT_REQUEST_CONFIG),
      ]);

      if (ratesResult.status === "fulfilled") {
        setRates(extractRatesPayload(ratesResult.value));
      } else if (ratesResult.reason?.response?.status === 404) {
        setRates([]);
      } else {
        setRates([]);
        nextLoadError =
          getErrorMessage(ratesResult.reason, "Failed to load business rates.")
      }

      if (subscriptionResult.status === "fulfilled") {
        setSubscription(extractSubscriptionPayload(subscriptionResult.value));
      } else if (subscriptionResult.reason?.response?.status === 404) {
        setSubscription(null);
      } else if (!nextLoadError) {
        setSubscription(null);
        nextLoadError =
          getErrorMessage(
            subscriptionResult.reason,
            "Failed to load subscription details."
          );
      }

      setLoadError(nextLoadError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPricingData();
  }, [loadPricingData]);

  const orderedRates = useMemo(() => {
    const orderMap = new Map(CATEGORY_ORDER.map((value, index) => [value, index]));
    return [...rates].sort((left, right) => {
      const leftIndex = orderMap.get(normalizeCategory(left?.category)) ?? 999;
      const rightIndex = orderMap.get(normalizeCategory(right?.category)) ?? 999;
      return leftIndex - rightIndex;
    });
  }, [rates]);

  const countryOptions = useMemo(() => {
    const optionsMap = new Map();

    orderedRates.forEach(item => {
      const key = getCountryKey(item);
      if (!optionsMap.has(key)) {
        optionsMap.set(key, {
          key,
          label: item?.countryName || item?.countryCode || COUNTRY_PLACEHOLDER,
        });
      }
    });

    return Array.from(optionsMap.values());
  }, [orderedRates]);

  useEffect(() => {
    if (countryOptions.length === 0) {
      if (selectedCountry !== "all") {
        setSelectedCountry("all");
      }
      return;
    }

    if (selectedCountry === "all" && countryOptions.length === 1) {
      setSelectedCountry(countryOptions[0].key);
      return;
    }

    if (
      selectedCountry !== "all" &&
      !countryOptions.some(option => option.key === selectedCountry)
    ) {
      setSelectedCountry(countryOptions[0].key);
    }
  }, [countryOptions, selectedCountry]);

  const countryLabel =
    orderedRates.find(item => item?.countryName)?.countryName ||
    orderedRates.find(item => item?.countryCode)?.countryCode ||
    COUNTRY_PLACEHOLDER;

  const selectedCountryLabel =
    selectedCountry === "all"
      ? countryOptions.length > 1
        ? "All Countries"
        : countryOptions.length === 1
          ? countryOptions[0].label
          : NO_COUNTRY_SELECTED
      : countryOptions.find(option => option.key === selectedCountry)?.label ||
        countryLabel;

  const filteredRates = useMemo(() => {
    if (selectedCountry === "all") return orderedRates;
    return orderedRates.filter(item => getCountryKey(item) === selectedCountry);
  }, [orderedRates, selectedCountry]);

  const currencyLabel =
    orderedRates.find(item => item?.currency)?.currency || "INR";

  const planName = subscription?.planName || subscription?.PlanName || "No active plan";
  const subscriptionStatus = formatSubscriptionStatus(
    subscription?.status ?? subscription?.Status
  );
  const billingCycle = formatBillingCycle(
    subscription?.billingCycle ?? subscription?.BillingCycle
  );
  const periodEnd = formatDate(
    subscription?.currentPeriodEndUtc || subscription?.CurrentPeriodEndUtc
  );
  const cancelAtPeriodEnd =
    subscription?.cancelAtPeriodEnd ?? subscription?.CancelAtPeriodEnd;
  const autoRenew = subscription?.autoRenew ?? subscription?.AutoRenew;
  const renewalState = cancelAtPeriodEnd
    ? "Ends this period"
    : autoRenew
      ? "Auto-renew enabled"
      : "Auto-renew off";
  const planBadge = [planName, subscriptionStatus !== "-" ? subscriptionStatus : null]
    .filter(Boolean)
    .join(" - ");
  const summaryItems = [
    { label: "Plan", value: planName },
    { label: "Billing", value: billingCycle },
    { label: "Renews", value: periodEnd },
    { label: "Renewal", value: renewalState },
    { label: "Country", value: selectedCountryLabel },
  ];

  return (
    <div className="min-h-[calc(100vh-88px)] bg-[#f6f8fb] p-4 sm:p-5">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600">
              <CircleDollarSign className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-[16px] font-semibold text-slate-900">
                Business Pricing
              </h1>
              <p className="text-sm text-slate-500">
                Final WhatsApp rates for your current business.
              </p>
            </div>
          </div>

          <div className="inline-flex h-8 items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700">
            {planBadge || "Plan unavailable"}
          </div>
        </div>

        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="grid divide-y divide-slate-100 bg-slate-50/60 sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-5">
            {summaryItems.map(item => (
              <SummaryCell key={item.label} label={item.label} value={item.value} />
            ))}
          </div>
        </section>

        <ErrorPanel message={loadError} onRetry={loadPricingData} />

        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">
                  Rate Card
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Country: <span className="font-semibold text-slate-700">{selectedCountryLabel}</span>
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  Country
                </span>
                <select
                  value={selectedCountry}
                  onChange={event => setSelectedCountry(event.target.value)}
                  className="h-9 min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                >
                  {countryOptions.length === 0 && (
                    <option value="all">{COUNTRY_PLACEHOLDER}</option>
                  )}
                  {countryOptions.length > 1 && (
                    <option value="all">All Countries</option>
                  )}
                  {countryOptions.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {isLoading ? (
            <LoadingRows />
          ) : orderedRates.length === 0 ? (
            <EmptyState />
          ) : filteredRates.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              No pricing data is available for the selected country.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-5 py-3">Category</th>
                    <th className="px-5 py-3">Country</th>
                    <th className="px-5 py-3 text-right">Price / Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {filteredRates.map(item => (
                    <tr
                      key={`${normalizeCategory(item.category)}-${item.countryCode || countryLabel}`}
                      className="hover:bg-slate-50"
                    >
                      <td className="px-5 py-3">
                        <span className="inline-flex rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-800">
                          {formatCategory(item.category)}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        {item.countryName || item.countryCode || countryLabel}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="font-semibold tabular-nums text-emerald-700">
                          {formatMessageRate(item.sellPrice, item.currency || currencyLabel)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center gap-2 border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
            <CalendarClock className="h-4 w-4" />
            <span>
              Cost per message in INR on the WhatsApp Business Platform,
              effective April 1, 2026
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}
